import { defineStore } from 'pinia';

import { Config } from '@/config';
import { isSamePage } from '@/helpers/pagination.helper';
import apiService from '@/services/api.service';
import type {
  SummaryFormDto,
  SummaryFormCommentDto,
  SummaryFormCommentPagination,
  SummaryFormOpenVoteDto,
  SummaryFormOpenVotePagination,
  SummaryFormOtherChoiceDto,
  SummaryFormOtherChoicePagination,
  SummaryFormStatDto,
  SummarySectionDto,
  SummaryExportingOptions,
} from '@/services/types/api.types';

export interface SummariesState {
  forms: Map<number, SummaryFormDto[]>;
  formComments: Map<number, SummaryFormCommentPagination>;
  formOpenVotes: Map<number, SummaryFormOpenVotePagination>;
  formOtherChoices: Map<number, SummaryFormOtherChoicePagination>;
  formStats: Map<number, SummaryFormStatDto>;
  itemLoadingStates: {
    forms: Map<number, boolean>;
    formComments: Map<number, boolean>;
    formOpenVotes: Map<number, boolean>;
    formOtherChoices: Map<number, boolean>;
    formStats: Map<number, boolean>;
    sections: boolean;
  };
  sections: SummarySectionDto[] | null;
}

const generateInitialState = (): SummariesState => ({
  forms: new Map<number, SummaryFormDto[]>(),
  formComments: new Map<number, SummaryFormCommentPagination>(),
  formOpenVotes: new Map<number, SummaryFormOpenVotePagination>(),
  formOtherChoices: new Map<number, SummaryFormOtherChoicePagination>(),
  formStats: new Map<number, SummaryFormStatDto>(),
  itemLoadingStates: {
    forms: new Map<number, boolean>(),
    formComments: new Map<number, boolean>(),
    formOpenVotes: new Map<number, boolean>(),
    formOtherChoices: new Map<number, boolean>(),
    formStats: new Map<number, boolean>(),
    sections: false,
  },
  sections: null,
});

export const useSummariesStore = defineStore('summaries', {
  state: generateInitialState,
  getters: {
    isFormsLoading: (state) => (sectionId: number) => state.itemLoadingStates.forms.get(sectionId) ?? false,
    isFormCommentsLoading: (state) => (formId: number) => state.itemLoadingStates.formComments.get(formId) ?? false,
    isFormOpenVotesLoading: (state) => (formId: number) => state.itemLoadingStates.formOpenVotes.get(formId) ?? false,
    isFormOtherChoicesLoading: (state) => (formId: number) =>
      state.itemLoadingStates.formOtherChoices.get(formId) ?? false,
    isFormStatsLoading: (state) => (formId: number) => state.itemLoadingStates.formStats.get(formId) ?? false,
    isSectionsLoading: (state) => () => state.itemLoadingStates.sections,
    formStatsById: (state) => (formId: number) => state.formStats.get(formId) ?? null,
  },
  actions: {
    async getForms(sectionId: number): Promise<SummaryFormDto[] | null> {
      if (!this.forms.get(sectionId) && !this.itemLoadingStates.forms.get(sectionId)) {
        this.itemLoadingStates.forms.set(sectionId, true);
        try {
          const { data } = await apiService.get<SummaryFormDto[]>(
            `${Config.api.v2}/summaries/sections/${sectionId}/forms`,
          );
          this.forms.set(sectionId, data);
        } catch (error) {
          console.error(error);
        } finally {
          this.itemLoadingStates.forms.set(sectionId, false);
        }
      }
      return this.forms.get(sectionId) ?? null;
    },
    async getFormComments(
      formId: number,
      page: number = 1,
      pageSize: number = 5,
    ): Promise<SummaryFormCommentPagination | null> {
      const pagination = this.formComments.get(formId)?.pagination;
      if (!isSamePage(pagination, page, pageSize) && !this.itemLoadingStates.formComments.get(formId)) {
        this.itemLoadingStates.formComments.set(formId, true);
        try {
          const { data, pagination } = await apiService.get<SummaryFormCommentDto[]>(
            `${Config.api.v2}/summaries/forms/${formId}/comments`,
            { page, pageSize },
          );
          this.formComments.set(formId, {
            data,
            pagination: { page: pagination!.currentPage, rowsNumber: pagination!.totalCount, rowsPerPage: pageSize },
          });
        } catch (error) {
          console.error(error);
        } finally {
          this.itemLoadingStates.formComments.set(formId, false);
        }
      }
      return this.formComments.get(formId) ?? null;
    },
    async getFormOpenVotes(
      formId: number,
      page: number = 1,
      pageSize: number = 5,
    ): Promise<SummaryFormOpenVotePagination | null> {
      const pagination = this.formOpenVotes.get(formId)?.pagination;
      if (!isSamePage(pagination, page, pageSize) && !this.itemLoadingStates.formOpenVotes.get(formId)) {
        this.itemLoadingStates.formOpenVotes.set(formId, true);
        try {
          const { data, pagination } = await apiService.get<SummaryFormOpenVoteDto[]>(
            `${Config.api.v2}/summaries/forms/${formId}/open-votes`,
            { page, pageSize },
          );
          this.formOpenVotes.set(formId, {
            data,
            pagination: { page: pagination!.currentPage, rowsNumber: pagination!.totalCount, rowsPerPage: pageSize },
          });
        } catch (error) {
          console.error(error);
        } finally {
          this.itemLoadingStates.formOpenVotes.set(formId, false);
        }
      }
      return this.formOpenVotes.get(formId) ?? null;
    },
    async getFormOtherChoices(
      formId: number,
      page: number = 1,
      pageSize: number = 5,
    ): Promise<SummaryFormOtherChoicePagination | null> {
      const pagination = this.formOtherChoices.get(formId)?.pagination;
      if (!isSamePage(pagination, page, pageSize) && !this.itemLoadingStates.formOtherChoices.get(formId)) {
        this.itemLoadingStates.formOtherChoices.set(formId, true);
        try {
          const { data, pagination } = await apiService.get<SummaryFormOtherChoiceDto[]>(
            `${Config.api.v2}/summaries/forms/${formId}/other-choices`,
            { page, pageSize },
          );
          this.formOtherChoices.set(formId, {
            data,
            pagination: { page: pagination!.currentPage, rowsNumber: pagination!.totalCount, rowsPerPage: pageSize },
          });
        } catch (error) {
          console.error(error);
        } finally {
          this.itemLoadingStates.formOtherChoices.set(formId, false);
        }
      }
      return this.formOtherChoices.get(formId) ?? null;
    },
    async getFormStats(formId: number): Promise<SummaryFormStatDto | null> {
      if (!this.formStats.get(formId) && !this.itemLoadingStates.formStats.get(formId)) {
        this.itemLoadingStates.formStats.set(formId, true);
        try {
          const { data } = await apiService.get<SummaryFormStatDto>(`${Config.api.v2}/summaries/forms/${formId}`);
          this.formStats.set(formId, data);
        } catch (error) {
          console.error(error);
        } finally {
          this.itemLoadingStates.formStats.set(formId, false);
        }
      }
      return this.formStats.get(formId) ?? null;
    },
    async getSections(consultationId: number): Promise<SummarySectionDto[] | null> {
      if (!this.sections && !this.itemLoadingStates.sections) {
        this.itemLoadingStates.sections = true;
        try {
          const { data } = await apiService.get<SummarySectionDto[]>(
            `${Config.api.v2}/summaries/consultations/${consultationId}/sections`,
          );
          this.sections = data;
        } catch (error) {
          console.error(error);
        } finally {
          this.itemLoadingStates.sections = false;
        }
      }
      return this.sections;
    },
    async resetToInitialState(): Promise<void> {
      this.$state = generateInitialState();
    },
    async export(options: SummaryExportingOptions) {
      try {
        const urlSearchParams = new URLSearchParams();
        if (options.consultationId) urlSearchParams.append('consultationId', options.consultationId.toString());
        if (options.sectionId) urlSearchParams.append('sectionId', options.sectionId.toString());
        if (options.formId) urlSearchParams.append('formId', options.formId.toString());
        return await apiService.get(`${Config.api.v2}/reports/summary?${urlSearchParams.toString()}`);
      } catch (error) {
        return { error, success: false };
      }
    },
  },
});
